import { z } from 'zod';
import { instantSchema, positiveSafeIntegerSchema, semverSchema, sha256Schema, } from './primitives.js';
export const indexedDbMigrationStepSchema = z
    .strictObject({
    migration_id: z.string().regex(/^indexeddb-[1-9]\d*-to-[1-9]\d*$/),
    from_schema_version: positiveSafeIntegerSchema,
    to_schema_version: positiveSafeIntegerSchema,
    implementation_version: semverSchema,
    strategy: z.literal('copy_transform_validate_atomic_swap'),
    source_database_access: z.literal('read_only'),
    target_database_kind: z.literal('staging_copy'),
    on_failure: z.literal('keep_previous_database'),
    rollback_supported: z.literal(true),
    fixture_sha256: z.array(sha256Schema).min(1).max(128),
    expected_schema_sha256: sha256Schema,
})
    .superRefine((value, context) => {
    if (value.to_schema_version !== value.from_schema_version + 1) {
        context.addIssue({
            code: 'custom',
            path: ['to_schema_version'],
            message: 'IndexedDB migrations must be explicit contiguous one-version steps',
        });
    }
    if (value.migration_id !== `indexeddb-${value.from_schema_version}-to-${value.to_schema_version}`) {
        context.addIssue({
            code: 'custom',
            path: ['migration_id'],
            message: 'migration_id must encode its exact version transition',
        });
    }
    if (new Set(value.fixture_sha256).size !== value.fixture_sha256.length) {
        context.addIssue({
            code: 'custom',
            path: ['fixture_sha256'],
            message: 'migration fixture checksums must be unique',
        });
    }
});
export const indexedDbMigrationPolicySchema = z
    .strictObject({
    contract_version: z.literal('indexeddb_migration_policy.v1'),
    policy_version: semverSchema,
    generated_at: instantSchema,
    minimum_supported_schema_version: positiveSafeIntegerSchema,
    current_schema_version: positiveSafeIntegerSchema,
    preflight_on_database_copy: z.literal(true),
    atomic_activation: z.literal(true),
    preserve_previous_until_validation: z.literal(true),
    migrations: z.array(indexedDbMigrationStepSchema).max(1_000),
})
    .superRefine((value, context) => {
    if (value.minimum_supported_schema_version > value.current_schema_version) {
        context.addIssue({
            code: 'custom',
            path: ['minimum_supported_schema_version'],
            message: 'minimum supported version cannot exceed current version',
        });
    }
    const expectedSteps = value.current_schema_version - value.minimum_supported_schema_version;
    if (value.migrations.length !== expectedSteps) {
        context.addIssue({
            code: 'custom',
            path: ['migrations'],
            message: 'migration policy must contain a complete contiguous path from minimum to current',
        });
        return;
    }
    value.migrations.forEach((migration, index) => {
        const expectedFrom = value.minimum_supported_schema_version + index;
        if (migration.from_schema_version !== expectedFrom ||
            migration.to_schema_version !== expectedFrom + 1) {
            context.addIssue({
                code: 'custom',
                path: ['migrations', index],
                message: 'migration steps must be ordered without skips or ambiguity',
            });
        }
    });
});
export const indexedDbMigrationExecutionSchema = z
    .strictObject({
    policy_version: semverSchema,
    source_schema_version: positiveSafeIntegerSchema,
    target_schema_version: positiveSafeIntegerSchema,
    source_database_name: z.string().min(1).max(256),
    staging_database_name: z.string().min(1).max(256),
    source_checkpoint_sha256: sha256Schema,
    started_at: instantSchema,
    validation_status: z.enum(['pending', 'passed', 'failed']),
    activation_status: z.enum(['not_started', 'activated', 'rolled_back']),
})
    .superRefine((value, context) => {
    if (value.target_schema_version <= value.source_schema_version) {
        context.addIssue({
            code: 'custom',
            path: ['target_schema_version'],
            message: 'migration target schema must be newer than the source schema',
        });
    }
    if (value.source_database_name === value.staging_database_name) {
        context.addIssue({
            code: 'custom',
            path: ['staging_database_name'],
            message: 'migration staging database must be physically separate from the source',
        });
    }
    if (value.activation_status === 'activated' && value.validation_status !== 'passed') {
        context.addIssue({
            code: 'custom',
            path: ['activation_status'],
            message: 'a migrated database can activate only after validation passes',
        });
    }
    if (value.activation_status === 'rolled_back' && value.validation_status !== 'failed') {
        context.addIssue({
            code: 'custom',
            path: ['activation_status'],
            message: 'rollback is evidence of a failed staged migration',
        });
    }
});
export function migrationPath(policyInput, fromSchemaVersionInput) {
    const policy = indexedDbMigrationPolicySchema.parse(policyInput);
    const fromSchemaVersion = positiveSafeIntegerSchema.parse(fromSchemaVersionInput);
    if (fromSchemaVersion < policy.minimum_supported_schema_version ||
        fromSchemaVersion > policy.current_schema_version) {
        return null;
    }
    return policy.migrations.filter((migration) => migration.from_schema_version >= fromSchemaVersion);
}
//# sourceMappingURL=migration.js.map